import React, { useCallback, useEffect, useRef, useState } from 'react';
import CreateListingPanel from './CreateListingPanel';
import { formatMoney } from './CertCells';
import './CertScanner.css';

// Camera scanner for PSA graded cards. Reads the label's barcode (1D on older labels,
// QR → psacard.com/cert/<n> on newer ones), and hands each new cert number to
// `addCert`, one at a time so we don't hammer PSA. Stays open between scans so
// a broker can run a whole stack through it.

const FORMATS = ['qr_code', 'code_128', 'code_39', 'code_93', 'codabar', 'itf', 'data_matrix'];
const SCAN_INTERVAL_MS = 70;
// Non-QR reads must agree twice in a row before we trust them (Code 39 has no checksum).
const CONFIRM_WINDOW_MS = 1200;
// A cert still in frame keeps getting read; only re-cue it after it's been gone this long.
const REAPPEAR_MS = 2500;
const CUE_MS = 1100;
const FEED_LIMIT = 60;

export const extractCertNumber = (raw) => {
    if (!raw) return null;
    const url = raw.match(/psacard\.com\/cert\/(\d+)/i);
    const digits = (url ? url[1] : raw.trim()).replace(/^0+/, '');
    return /^\d{5,10}$/.test(digits) ? digits : null;
};

// Prefer the browser's own detector; fall back to the zxing-wasm ponyfill (lazy-loaded).
async function createDetector() {
    if ('BarcodeDetector' in window) {
        try {
            const supported = await window.BarcodeDetector.getSupportedFormats();
            const formats = FORMATS.filter((f) => supported.includes(f));
            if (formats.includes('qr_code') && formats.includes('code_128')) {
                return new window.BarcodeDetector({ formats });
            }
        } catch { /* fall through to ponyfill */ }
    }
    const { BarcodeDetector } = await import('barcode-detector/ponyfill');
    return new BarcodeDetector({ formats: FORMATS });
}

// ---- Audio cues (WebAudio, no assets) ----
let audioCtx = null;
function playCue(kind) {
    try {
        audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        const notes = {
            success: [[1046, 0, 0.07], [1568, 0.075, 0.11]],
            duplicate: [[660, 0, 0.12]],
            error: [[220, 0, 0.09], [196, 0.11, 0.14]],
        }[kind];
        const t0 = audioCtx.currentTime;
        notes.forEach(([freq, start, dur]) => {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.type = kind === 'error' ? 'triangle' : 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, t0 + start);
            gain.gain.exponentialRampToValueAtTime(0.18, t0 + start + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
            osc.connect(gain).connect(audioCtx.destination);
            osc.start(t0 + start);
            osc.stop(t0 + start + dur + 0.02);
        });
    } catch { /* audio is a nicety */ }
    try { navigator.vibrate?.(kind === 'success' ? 40 : kind === 'error' ? [60, 40, 60] : 20); } catch { /* ignore */ }
}

// ---- Icons ----
const Icon = {
    close: <path d="M6 6l12 12M18 6L6 18" />,
    check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    alert: <><path d="M12 8v5" /><path d="M12 16.5v.01" /><circle cx="12" cy="12" r="9" /></>,
    torch: <path d="M9 2h6l-1 6h3l-7 14 1-9H8z" />,
    camera: <><path d="M3 8a2 2 0 012-2h2l1.5-2h7L17 6h2a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><circle cx="12" cy="13" r="3.5" /></>,
};
const Svg = ({ name, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {Icon[name]}
    </svg>
);

const isListed = (cert) => !!cert?.listings?.some((l) => l.is_active);

// The cert's live listing, if it has one we can PATCH.
const activeListing = (cert) => cert?.listings?.find((l) => l.is_active && l.listing_id) || null;

const CertScanner = ({ open, onClose, addCert, isOwned, getCert, listCert, updateListing }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const detectorRef = useRef(null);
    const queueRef = useRef([]);
    const workingRef = useRef(false);
    const seenRef = useRef(new Map()); // cert → last time it was in frame
    const pendingRef = useRef(null); // { cert, at } awaiting a confirming read
    const cueTimer = useRef(null);
    const flashTimer = useRef(null);

    const [camState, setCamState] = useState('starting'); // starting | live | denied | unsupported | error
    const [cue, setCue] = useState('idle'); // idle | success | duplicate | error
    const [lastRead, setLastRead] = useState(null);
    const [outline, setOutline] = useState(null);
    const [feed, setFeed] = useState([]);
    const [mirrored, setMirrored] = useState(false);
    const [torch, setTorch] = useState({ supported: false, on: false });
    const [flashId, setFlashId] = useState(null);
    const [restartKey, setRestartKey] = useState(0);
    // Camera released (light off). Clicking the camera view toggles it, and leaving
    // the browser tab pauses it automatically.
    const [paused, setPaused] = useState(false);
    useEffect(() => { if (!open) setPaused(false); }, [open]);
    const feedRef = useRef([]);
    const feedListRef = useRef(null);
    const mirroredRef = useRef(false);
    useEffect(() => { mirroredRef.current = mirrored; }, [mirrored]);

    // Feed lives in a ref too so detection callbacks can read it synchronously.
    const updateFeed = useCallback((fn) => {
        feedRef.current = fn(feedRef.current);
        setFeed(feedRef.current);
    }, []);

    // Keep the latest callbacks without restarting the camera when the parent re-renders.
    const addCertRef = useRef(addCert);
    const isOwnedRef = useRef(isOwned);
    const getCertRef = useRef(getCert);
    useEffect(() => { addCertRef.current = addCert; isOwnedRef.current = isOwned; getCertRef.current = getCert; });
    // Cert open in the create-listing panel (full cert object), if any.
    const [listingCert, setListingCert] = useState(null);
    const listingOpenRef = useRef(false);
    useEffect(() => { listingOpenRef.current = !!listingCert; }, [listingCert]);
    useEffect(() => { if (!open) setListingCert(null); }, [open]);

    const signal = useCallback((kind, cert) => {
        setCue(kind);
        if (cert) setLastRead({ cert, kind, at: Date.now() });
        playCue(kind);
        clearTimeout(cueTimer.current);
        cueTimer.current = setTimeout(() => setCue('idle'), CUE_MS);
    }, []);

    const flashItem = useCallback((cert) => {
        setFlashId(cert);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlashId(null), 1200);
    }, []);

    const patchItem = useCallback((cert, patch) =>
        updateFeed((prev) => prev.map((it) => (it.cert === cert ? { ...it, ...patch } : it))), [updateFeed]);

    // Sequential worker: one POST /cert at a time.
    const pump = useCallback(async () => {
        if (workingRef.current) return;
        workingRef.current = true;
        while (queueRef.current.length) {
            const cert = queueRef.current.shift();
            patchItem(cert, { status: 'adding' });
            try {
                const created = await addCertRef.current(cert);
                patchItem(cert, { status: 'added', data: created, listed: isListed(created) });
                // A panel is open and this is the newest scan — switch the panel to it.
                if (listingOpenRef.current && feedRef.current[0]?.cert === cert) {
                    setListingCert(getCertRef.current?.(cert) || created);
                }
            } catch (e) {
                patchItem(cert, { status: 'error', error: e.message });
                signal('error', cert);
            }
        }
        workingRef.current = false;
    }, [signal, patchItem]);

    const enqueue = useCallback((cert) => {
        if (queueRef.current.includes(cert)) return;
        updateFeed((prev) => [{ cert, status: 'queued', at: Date.now() }, ...prev.filter((it) => it.cert !== cert)]);
        queueRef.current.push(cert);
        pump();
    }, [pump, updateFeed]);

    const handleCert = useCallback((cert) => {
        const now = Date.now();
        const lastSeen = seenRef.current.get(cert);
        seenRef.current.set(cert, now);
        // Still holding the same graded card up — stay quiet.
        if (lastSeen && now - lastSeen < REAPPEAR_MS) return;

        const existing = feedRef.current.find((it) => it.cert === cert);
        if (existing?.status === 'error') {
            // Re-scanning a failed one is an implicit retry.
            signal('success', cert);
            enqueue(cert);
        } else if (existing) {
            // Already in the feed — treat the rescan as a search: bring the row back
            // to the top and open it in the panel (list it, or edit its listing).
            signal('duplicate', cert);
            updateFeed((prev) => [{ ...existing, at: now }, ...prev.filter((it) => it.cert !== cert)]);
            feedListRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
            flashItem(cert);
            const data = getCertRef.current?.(cert) || existing.data;
            if (data && (existing.status === 'added' || existing.status === 'owned')) setListingCert(data);
        } else if (isOwnedRef.current(cert)) {
            signal('duplicate', cert);
            const data = getCertRef.current?.(cert) || null;
            const listed = isListed(data);
            updateFeed((prev) => [{ cert, status: 'owned', at: now, data, listed }, ...prev]);
            flashItem(cert);
            // Already ours and not listed yet — that's why it's being scanned: open the listing panel.
            // If another cert is open in the panel, switch to this newest scan either way.
            if (data && (!listed || listingOpenRef.current)) setListingCert(data);
        } else {
            signal('success', cert);
            enqueue(cert);
        }
    }, [enqueue, signal, flashItem, updateFeed]);

    // Map a detection's corner points from video pixels onto the (object-fit: cover) element.
    const showOutline = useCallback((points) => {
        const v = videoRef.current;
        if (!v || !points?.length || !v.videoWidth) return;
        const cw = v.clientWidth;
        const ch = v.clientHeight;
        const scale = Math.max(cw / v.videoWidth, ch / v.videoHeight);
        const ox = (cw - v.videoWidth * scale) / 2;
        const oy = (ch - v.videoHeight * scale) / 2;
        const pts = points.map(({ x, y }) => {
            const px = x * scale + ox;
            return `${mirroredRef.current ? cw - px : px},${y * scale + oy}`;
        });
        setOutline({ pts: pts.join(' '), w: cw, h: ch, at: Date.now() });
    }, []);

    // ---- Camera lifecycle ----
    useEffect(() => {
        if (!open || paused) return undefined;
        let cancelled = false;
        let timer = null;
        let wakeLock = null;

        const stop = () => {
            clearTimeout(timer);
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            try { wakeLock?.release(); } catch { /* ignore */ }
        };

        const loop = async () => {
            if (cancelled) return;
            const v = videoRef.current;
            if (v && v.readyState >= 2 && detectorRef.current) {
                try {
                    const found = await detectorRef.current.detect(v);
                    for (const code of found) {
                        const cert = extractCertNumber(code.rawValue);
                        if (!cert) continue;
                        showOutline(code.cornerPoints);
                        const now = Date.now();
                        const pending = pendingRef.current;
                        const confirmed = code.format === 'qr_code'
                            || (pending && pending.cert === cert && now - pending.at < CONFIRM_WINDOW_MS);
                        if (confirmed) {
                            pendingRef.current = null;
                            handleCert(cert);
                        } else {
                            pendingRef.current = { cert, at: now };
                        }
                        break;
                    }
                } catch { /* a bad frame — keep going */ }
            }
            timer = setTimeout(loop, SCAN_INTERVAL_MS);
        };

        (async () => {
            setCamState('starting');
            if (!navigator.mediaDevices?.getUserMedia) {
                setCamState('unsupported');
                return;
            }
            try {
                const [stream, detector] = await Promise.all([
                    navigator.mediaDevices.getUserMedia({
                        audio: false,
                        video: {
                            facingMode: { ideal: 'environment' },
                            width: { ideal: 1920 },
                            height: { ideal: 1080 },
                        },
                    }),
                    detectorRef.current ? Promise.resolve(detectorRef.current) : createDetector(),
                ]);
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                detectorRef.current = detector;

                const track = stream.getVideoTracks()[0];
                const settings = track.getSettings?.() || {};
                const caps = track.getCapabilities?.() || {};
                setMirrored(settings.facingMode !== 'environment');
                setTorch({ supported: !!caps.torch, on: false });
                if (caps.focusMode?.includes('continuous')) {
                    track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
                }

                const v = videoRef.current;
                v.srcObject = stream;
                await v.play().catch(() => {});
                setCamState('live');

                loop();
                // Keep the screen awake through a long stack of graded cards.
                try {
                    const lock = await navigator.wakeLock?.request('screen');
                    if (cancelled) lock?.release(); else wakeLock = lock;
                } catch { /* ignore */ }
            } catch (e) {
                if (cancelled) return;
                setCamState(e?.name === 'NotAllowedError' || e?.name === 'SecurityError' ? 'denied' : 'error');
            }
        })();

        return () => { cancelled = true; stop(); };
    }, [open, paused, restartKey, handleCert, showOutline]);

    // Fade the barcode outline shortly after the last read.
    useEffect(() => {
        if (!outline) return undefined;
        const t = setTimeout(() => setOutline(null), 350);
        return () => clearTimeout(t);
    }, [outline]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !listingOpenRef.current) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    useEffect(() => () => { clearTimeout(cueTimer.current); clearTimeout(flashTimer.current); }, []);

    // Leaving the browser tab pauses the scanner: the camera is released (light
    // off) but the panel stays open until it's clicked to resume.
    useEffect(() => {
        if (!open) return undefined;
        const onVisibility = () => { if (document.hidden) setPaused(true); };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        document.body.classList.add('cert-scanner-open');
        return () => document.body.classList.remove('cert-scanner-open');
    }, [open]);

    const toggleTorch = async () => {
        const track = streamRef.current?.getVideoTracks()[0];
        if (!track) return;
        const on = !torch.on;
        try {
            await track.applyConstraints({ advanced: [{ torch: on }] });
            setTorch((t) => ({ ...t, on }));
        } catch { /* ignore */ }
    };

    const closeListing = useCallback(() => setListingCert(null), []);

    // Hovering a row opens its listing panel (after a short pause, so sweeping the
    // pointer across the list doesn't flicker). Never swap the cert out from
    // under someone who's working in the panel.
    const hoverTimer = useRef(null);
    const hoverRow = (data) => {
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => {
            if (document.activeElement?.closest?.('.create-listing-panel')) return;
            setListingCert(data);
        }, 120);
    };
    const leaveRow = () => clearTimeout(hoverTimer.current);
    useEffect(() => () => clearTimeout(hoverTimer.current), []);

    // After listing/updating, refresh the row's cert from the tab's owned map so it
    // carries the real listing (id, price) — that's what makes it editable.
    const handleList = useCallback(async ({ cert, price, autoPrice }) => {
        await listCert({ cert, price, autoPrice });
        const key = String(cert.cert_number);
        patchItem(key, { listed: true, data: getCertRef.current?.(key) || cert });
    }, [listCert, patchItem]);

    const handleUpdate = useCallback(async ({ cert, listing, price, autoPrice }) => {
        await updateListing({ cert, listing, price, autoPrice });
        const key = String(cert.cert_number);
        patchItem(key, { data: getCertRef.current?.(key) || cert });
    }, [updateListing, patchItem]);

    const openEdit = (certNumber, data) => {
        clearTimeout(hoverTimer.current);
        setListingCert(getCertRef.current?.(certNumber) || data);
    };

    if (!open) return null;

    const showRead = lastRead && cue !== 'idle';
    const canToggle = paused || camState === 'live';
    const togglePause = () => setPaused((p) => !p);

    return (
        <div className="cert-scanner-layer" role="dialog" aria-label="Scan PSA certs">
            <div className={`cert-scanner-panel cue-${cue}`}>
                {/* The whole camera view is the pause/resume toggle. */}
                <div
                    className={`cert-scanner-view ${canToggle ? 'toggleable' : ''}`}
                    onClick={canToggle ? togglePause : undefined}
                    onKeyDown={canToggle ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePause(); }
                    } : undefined}
                    role={canToggle ? 'button' : undefined}
                    tabIndex={canToggle ? 0 : undefined}
                    aria-label={canToggle ? (paused ? 'Resume scanning' : 'Pause scanning') : undefined}
                >
                    <video
                        ref={videoRef}
                        className={mirrored ? 'mirrored' : ''}
                        muted
                        playsInline
                        autoPlay
                    />

                    <button
                        type="button"
                        className="cert-scanner-close"
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        aria-label="Close scanner"
                        title="Close (Esc)"
                    >
                        <Svg name="close" size={18} />
                    </button>

                    {paused && (
                        <div className="cert-scanner-paused" aria-hidden="true">
                            <span className="cert-scanner-paused-title">Paused</span>
                            <span>Click to resume</span>
                        </div>
                    )}
                    {!paused && camState === 'live' && (
                        <span className="cert-scanner-hint" aria-hidden="true">Click to pause</span>
                    )}

                    {!paused && camState === 'live' && (
                        <>
                            <div className="cert-scanner-reticle" aria-hidden="true">
                                <i /><i /><i /><i />
                                <span className="cert-scanner-sweep" />
                            </div>
                            {outline && (
                                <svg className="cert-scanner-outline" width={outline.w} height={outline.h} aria-hidden="true">
                                    <polygon points={outline.pts} />
                                </svg>
                            )}
                            <div className="cert-scanner-flash" aria-hidden="true" />
                            {showRead && (
                                <div key={lastRead.at} className={`cert-scanner-read read-${lastRead.kind}`}>
                                    <Svg name={lastRead.kind === 'success' ? 'check' : lastRead.kind === 'duplicate' ? 'check' : 'alert'} size={16} />
                                    <span>{lastRead.cert}</span>
                                </div>
                            )}
                        </>
                    )}

                    {!paused && camState !== 'live' && (
                        <div className={`cert-scanner-state state-${camState}`}>
                            {camState === 'starting' ? (
                                <span className="cert-scanner-spinner large" />
                            ) : (
                                <>
                                    <Svg name="camera" size={34} />
                                    <span>
                                        {camState === 'denied' && 'Allow camera access to scan'}
                                        {camState === 'unsupported' && 'Camera not available here'}
                                        {camState === 'error' && 'Couldn’t start the camera'}
                                    </span>
                                    {camState !== 'unsupported' && (
                                        <button type="button" className="cert-scanner-pill-btn"
                                            onClick={(e) => { e.stopPropagation(); setRestartKey((k) => k + 1); }}>
                                            Retry
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {torch.supported && (
                    <div className="cert-scanner-controls">
                        <button type="button" className={`cert-scanner-icon-btn ${torch.on ? 'active' : ''}`}
                            onClick={toggleTorch} aria-label="Toggle light" title="Light">
                            <Svg name="torch" />
                        </button>
                    </div>
                )}

                <ul className="cert-scanner-feed" ref={feedListRef}>
                    {feed.length === 0 && (
                        <li className="cert-scanner-feed-empty" aria-hidden="true">
                            <span className="ghost-thumb" /><span className="ghost-line" />
                        </li>
                    )}
                    {feed.slice(0, FEED_LIMIT).map((it) => {
                        const image = it.data?.cert_images?.front_image_url;
                        const details = [it.data?.card?.name || it.data?.name, it.data?.grade].filter(Boolean).join(', ');
                        const canList = (it.status === 'added' || it.status === 'owned') && it.data && !it.listed;
                        const listing = it.listed ? activeListing(getCert?.(it.cert) || it.data) : null;
                        const canEdit = !!listing;
                        return (
                            <li
                                key={it.cert}
                                className={[
                                    'cert-scanner-item',
                                    `status-${it.status}`,
                                    flashId === it.cert && 'flash',
                                    listingCert && String(listingCert.cert_number) === it.cert && 'selected',
                                    canList && 'listable',
                                    canEdit && 'editable',
                                ].filter(Boolean).join(' ')}
                                onMouseEnter={canList ? () => hoverRow(it.data) : undefined}
                                onMouseLeave={canList ? leaveRow : undefined}
                                onClick={canEdit ? () => openEdit(it.cert, it.data) : undefined}
                                title={canEdit ? 'Click to edit this listing' : undefined}
                            >
                                <span className="cert-scanner-thumb">
                                    {image ? <img src={image} alt="" /> : <Svg name="camera" size={14} />}
                                </span>
                                <span className="cert-scanner-item-main">
                                    <span className="cert-scanner-item-cert">{it.cert}</span>
                                    <span className="cert-scanner-item-sub" title={it.error || details}>
                                        {it.status === 'error' ? (it.error || 'Failed') : details}
                                    </span>
                                </span>
                                <span className="cert-scanner-item-status">
                                    {(it.status === 'queued' || it.status === 'adding') && <span className="cert-scanner-spinner" />}
                                    {canList && (
                                        <span className="cert-scanner-list-hint" aria-hidden="true">
                                            List
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14M13 6l6 6-6 6" />
                                            </svg>
                                        </span>
                                    )}
                                    {listing?.price != null && (
                                        <span className="cert-scanner-listed-price">{formatMoney(listing.price)}</span>
                                    )}
                                    {it.listed && (
                                        <span className="cert-scanner-listed"><Svg name="check" size={12} /> Listed</span>
                                    )}
                                    {it.status === 'error' && (
                                        <button type="button" className="cert-scanner-retry-btn" onClick={() => enqueue(it.cert)}>
                                            Retry
                                        </button>
                                    )}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <CreateListingPanel
                cert={listingCert}
                listing={activeListing(listingCert)}
                onClose={closeListing}
                onList={handleList}
                onUpdate={handleUpdate}
            />
        </div>
    );
};

export default CertScanner;
